import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Grid,
  Card,
  CardActionArea,
  CardContent,
  Typography,
  Avatar,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import BuildIcon from "@mui/icons-material/Build";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SecurityIcon from "@mui/icons-material/Security";
import StorageIcon from "@mui/icons-material/Storage";
import BackupIcon from "@mui/icons-material/Backup";

import MigaPan from "../Componentes/MigaPan";
import Titulo from "../Componentes/Titulo";
import CargandoDatos from "../Componentes/CargandoDatos";
import AxiosRecibir from "../Componentes/AxiosRecibir";

const stripHTML = (html) => {
  if (!html) return "";
  const tmp = document.createElement("div");
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || "";
};

const IntervencionCard = ({ intervencion, onNavigate }) => (
  <Card
    variant="outlined"
    sx={{
      height: "100%",
      transition: "box-shadow 0.15s, border-color 0.15s",
      "&:hover": { boxShadow: 3, borderColor: "primary.main" },
    }}
  >
    <CardActionArea onClick={() => onNavigate(intervencion.parte.id)} sx={{ height: "100%" }}>
      <CardContent sx={{ display: "flex", gap: 1.5, alignItems: "center", py: "10px !important", px: 1.5 }}>
        <Avatar
          src={`https://api.sat.cloudlsf.es/imagenes/trabajadores/${intervencion.tecnico_id}.jpg`}
          alt="Técnico"
          sx={{ width: 36, height: 36, flexShrink: 0 }}
        >
          <BuildIcon fontSize="small" />
        </Avatar>

        <Box sx={{ flex: 1, overflow: "hidden" }}>
          <Typography variant="caption" color="primary" fontWeight="bold">
            #{intervencion.parte.codigo}
          </Typography>
          <Typography variant="body2" fontWeight="bold" noWrap title={intervencion.parte.cliente.nombre}>
            {intervencion.parte.cliente.nombre || "Sin cliente"}
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "-webkit-box", WebkitLineClamp: 1, WebkitBoxOrient: "vertical", overflow: "hidden" }}
          >
            {stripHTML(intervencion.parte.incidencia)}
          </Typography>
        </Box>
      </CardContent>
    </CardActionArea>
  </Card>
);

const TecnicoCard = ({ tecnico }) => (
  <Card
    variant="outlined"
    sx={{
      height: "100%",
      transition: "box-shadow 0.15s, border-color 0.15s",
      "&:hover": { boxShadow: 3, borderColor: "primary.main" },
    }}
  >
    <CardContent sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5, py: "10px !important", px: 1.5 }}>
      <Avatar
        src={`https://api.sat.cloudlsf.es/imagenes/trabajadores/${tecnico.id}.jpg`}
        alt="Técnico"
        sx={{ width: 36, height: 36 }}
      >
        <BuildIcon fontSize="small" />
      </Avatar>
      <Chip label={tecnico.cantidad} size="small" color="primary" sx={{ height: 18, fontSize: 11, fontWeight: "bold" }} />
    </CardContent>
  </Card>
);

const FilaProblema = ({ linea1, linea2, badge }) => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      py: 1,
      px: 1,
      borderBottom: "1px solid",
      borderColor: "divider",
      "&:last-child": { borderBottom: "none" },
    }}
  >
    <Box>
      <Typography variant="body2" fontWeight="bold">{linea1}</Typography>
      <Typography variant="caption" color="text.secondary">{linea2}</Typography>
    </Box>
    {badge}
  </Box>
);

const Home = () => {
  const [intervencionesActivas, setIntervencionesActivas] = useState([]);
  const [cargaTrabajo, setCargaTrabajo] = useState([]);
  const [antivirusProblemas, setAntivirusProblemas] = useState({ disabled: [], outdated: [], discos_llenos: [] });
  const [backupsAlarma, setBackupsAlarma] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchIntervenciones = async () => {
      const response = await AxiosRecibir({ endpoint: `/partes/listadoIntervencionesActivas` });
      setIntervencionesActivas(response.data);
    };

    const fetchCargaTrabajo = async () => {
      const response = await AxiosRecibir({ endpoint: `/tecnicos/cargaTrabajo` });
      setCargaTrabajo(response.data);
    };

    const fetchAntivirusProblemas = async () => {
      const response = await AxiosRecibir({ endpoint: `/glpi/antivirus/problemas` });
      setAntivirusProblemas(response.data);
    };

    const fetchBackupsAlarma = async () => {
      const response = await AxiosRecibir({ endpoint: `/backups/dashboard` });
      setBackupsAlarma(response.data?.alarma ?? []);
    };

    const fetchData = async () => {
      try {
        await Promise.all([fetchIntervenciones(), fetchCargaTrabajo(), fetchAntivirusProblemas(), fetchBackupsAlarma()]);
      } catch (err) {
        console.error("Error al cargar datos:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) return <CargandoDatos />;

  const { disabled, outdated, discos_llenos } = antivirusProblemas;

  return (
    <Box className="box-contenido-alineado-izquierda" sx={{ padding: 2 }}>
      <MigaPan breadcrumbs={[{ label: "Home", href: "/" }]} />

      <Titulo texto="Trabajadores Activos" />
      <Grid container spacing={2} padding={2}>
        {intervencionesActivas.length > 0 ? (
          intervencionesActivas.map((intervencion) => (
            <Grid item xs={12} sm={6} md={4} key={intervencion.id}>
              <IntervencionCard
                intervencion={intervencion}
                onNavigate={(id) => navigate(`/Partes/DetalleParte/${id}`)}
              />
            </Grid>
          ))
        ) : (
          <Grid item xs={12}>
            <Typography color="text.secondary">No hay trabajadores activos.</Typography>
          </Grid>
        )}
      </Grid>

      <Titulo texto="Carga de Trabajo de Técnicos" />
      <Grid container spacing={2} padding={2}>
        {cargaTrabajo.map((tecnico) => (
          <Grid item xs={4} sm={2} md={2} xl={1} key={tecnico.id}>
            <TecnicoCard tecnico={tecnico} />
          </Grid>
        ))}
      </Grid>

      <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 1.5 }}>
        <Accordion sx={{ borderLeft: 4, borderColor: "error.main" }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <SecurityIcon color="error" />
              <Typography fontWeight="bold">Antivirus desactivado</Typography>
              {disabled.length > 0 && (
                <Chip label={disabled.length} color="error" size="small" sx={{ fontWeight: "bold" }} />
              )}
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 0 }}>
            {disabled.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                Sin equipos con antivirus desactivado.
              </Typography>
            ) : (
              disabled.map((av) => (
                <FilaProblema
                  key={av.id}
                  linea1={av.computer?.name ?? "—"}
                  linea2={`${av.computer?.cliente?.nombre ?? "Sin cliente"} · ${av.name} ${av.version ?? ""}`.trim()}
                  badge={<Chip label="Desactivado" color="error" size="small" />}
                />
              ))
            )}
          </AccordionDetails>
        </Accordion>

        <Accordion sx={{ borderLeft: 4, borderColor: "warning.main" }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <SecurityIcon color="warning" />
              <Typography fontWeight="bold">Antivirus desactualizado</Typography>
              {outdated.length > 0 && (
                <Chip label={outdated.length} color="warning" size="small" sx={{ fontWeight: "bold" }} />
              )}
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 0 }}>
            {outdated.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                Sin equipos con antivirus desactualizado.
              </Typography>
            ) : (
              outdated.map((av) => (
                <FilaProblema
                  key={av.id}
                  linea1={av.computer?.name ?? "—"}
                  linea2={`${av.computer?.cliente?.nombre ?? "Sin cliente"} · ${av.name} ${av.version ?? ""}`.trim()}
                  badge={<Chip label="Desactualizado" color="warning" size="small" />}
                />
              ))
            )}
          </AccordionDetails>
        </Accordion>

        <Accordion sx={{ borderLeft: 4, borderColor: "error.main" }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <StorageIcon color="error" />
              <Typography fontWeight="bold">Discos con más del 80% de ocupación</Typography>
              {discos_llenos.length > 0 && (
                <Chip label={discos_llenos.length} color="error" size="small" sx={{ fontWeight: "bold" }} />
              )}
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 0 }}>
            {discos_llenos.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                Sin discos con ocupación elevada.
              </Typography>
            ) : (
              discos_llenos.map((disco) => {
                const pct = disco.total > 0 ? Math.round(((disco.total - disco.free) / disco.total) * 100) : 0;
                return (
                  <FilaProblema
                    key={disco.id}
                    linea1={`${disco.computer?.name ?? "—"} · Unidad ${disco.letter}`}
                    linea2={`${disco.computer?.cliente?.nombre ?? "Sin cliente"} · Libre: ${disco.free} MB de ${disco.total} MB`}
                    badge={<Chip label={`${pct}%`} color="error" size="small" sx={{ fontWeight: "bold" }} />}
                  />
                );
              })
            )}
          </AccordionDetails>
        </Accordion>

        <Accordion sx={{ borderLeft: 4, borderColor: "warning.main" }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <BackupIcon color="warning" />
              <Typography fontWeight="bold">Backups sin recibir</Typography>
              {backupsAlarma.length > 0 && (
                <Chip label={backupsAlarma.length} color="warning" size="small" sx={{ fontWeight: "bold" }} />
              )}
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 0 }}>
            {backupsAlarma.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                Todos los backups recibidos dentro de la tolerancia.
              </Typography>
            ) : (
              Object.entries(
                backupsAlarma
                  .slice()
                  .sort((a, b) => (a.cliente_nombre ?? "").localeCompare(b.cliente_nombre ?? "", "es"))
                  .reduce((grupos, b) => {
                    const key = b.cliente_nombre ?? "Sin cliente";
                    (grupos[key] ??= []).push(b);
                    return grupos;
                  }, {})
              ).map(([cliente, items]) => (
                <Box key={cliente}>
                  <Typography
                    variant="caption"
                    fontWeight="bold"
                    color="text.secondary"
                    sx={{ display: "block", px: 1.5, pt: 1, pb: 0.5, textTransform: "uppercase", letterSpacing: 0.5 }}
                  >
                    {cliente}
                  </Typography>
                  {items.map((b) => (
                    <FilaProblema
                      key={b.id}
                      linea1={b.patron_asunto ?? "—"}
                      linea2={`Último: ${b.ultima_recepcion ? new Date(b.ultima_recepcion).toLocaleDateString("es-ES") : "Nunca"} · Tolerancia: ${b.dias_tolerancia}d`}
                      badge={<Chip label={`${b.dias_sin_reportar}d`} color="warning" size="small" sx={{ fontWeight: "bold" }} />}
                    />
                  ))}
                </Box>
              ))
            )}
          </AccordionDetails>
        </Accordion>
      </Box>
    </Box>
  );
};

export default Home;
